import { Status, Wrapper } from '@googlemaps/react-wrapper'
import { useEffect, useRef } from 'react'
import { ISiteNetworkGraphFeature } from 'types/api/siteNetworkApi.interface'
import { INetworkMapProps } from 'types/siteNetwork.interface'
import { defaultRoadClassColor, roadClassColors } from './siteNetworkData'
import { MapLoading, MapShell } from './NetworkMap.styled'

const mapOptions: google.maps.MapOptions = {
  gestureHandling: 'greedy',
  zoom: 3,
  center: { lat: 0, lng: 0 },
  backgroundColor: '#141312',
  disableDefaultUI: true,
  zoomControl: true,
  disableDoubleClickZoom: true,
  mapTypeId: 'satellite',
}

const toLatLng = (coord: number[]): google.maps.LatLngLiteral => ({ lat: coord[1], lng: coord[0] })

const toPath = (coordinates: number[][]): google.maps.LatLngLiteral[] => coordinates.map(toLatLng)

const dashIcons = (color: string, weight: number): google.maps.IconSequence[] => [
  {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: 1,
      strokeColor: color,
      strokeWeight: weight,
      scale: 3,
    },
    offset: '0',
    repeat: '16px',
  },
]

const lineStrings = (graph: INetworkMapProps['graph']): ISiteNetworkGraphFeature[] =>
  (graph?.features ?? []).filter((f) => f.geometry.type === 'LineString')

const points = (graph: INetworkMapProps['graph']): ISiteNetworkGraphFeature[] =>
  (graph?.features ?? []).filter((f) => f.geometry.type === 'Point')

const NetworkMapInner = ({
  graph,
  draftGraph,
  closedSegmentIds = [],
  routeGeometry,
  proposalGeometry,
  drawCoordinates = [],
  mode = 'view',
  onSegmentClick,
  onMapClick,
  onMapDoubleClick,
}: INetworkMapProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  const overlaysRef = useRef<google.maps.MVCObject[]>([])
  const listenersRef = useRef<google.maps.MapsEventListener[]>([])
  const fittedRef = useRef(false)

  const modeRef = useRef(mode)
  const onSegmentClickRef = useRef(onSegmentClick)
  const onMapClickRef = useRef(onMapClick)
  const onMapDoubleClickRef = useRef(onMapDoubleClick)

  modeRef.current = mode
  onSegmentClickRef.current = onSegmentClick
  onMapClickRef.current = onMapClick
  onMapDoubleClickRef.current = onMapDoubleClick

  const clearOverlays = () => {
    overlaysRef.current.forEach((overlay) => {
      if ('setMap' in overlay && typeof overlay.setMap === 'function') {
        overlay.setMap(null)
      }
    })
    overlaysRef.current = []
  }

  const clearListeners = () => {
    listenersRef.current.forEach((listener) => listener.remove())
    listenersRef.current = []
  }

  useEffect(() => {
    if (!ref.current || mapRef.current) return
    mapRef.current = new google.maps.Map(ref.current, mapOptions)

    const clickListener = mapRef.current.addListener('click', (e: google.maps.MapMouseEvent) => {
      if (modeRef.current === 'draw' && e.latLng) {
        onMapClickRef.current?.([e.latLng.lng(), e.latLng.lat()])
      }
    })
    const dblListener = mapRef.current.addListener('dblclick', (e: google.maps.MapMouseEvent) => {
      if (modeRef.current === 'draw' && e.latLng) {
        onMapDoubleClickRef.current?.([e.latLng.lng(), e.latLng.lat()])
      }
    })
    listenersRef.current = [clickListener, dblListener]

    return clearListeners
  }, [])

  const closedKey = closedSegmentIds.join(',')
  const drawKey = drawCoordinates.map((c) => c.join(',')).join(';')

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    clearOverlays()

    const segments = lineStrings(graph)
    const isInteractive = mode === 'view'

    if (!fittedRef.current && segments.length) {
      const bounds = new google.maps.LatLngBounds()
      segments.forEach((f) =>
        (f.geometry.coordinates as number[][]).forEach((c) => bounds.extend(toLatLng(c))),
      )
      if (!bounds.isEmpty()) {
        map.fitBounds(bounds, 60)
        fittedRef.current = true
      }
    }

    segments.forEach((feature) => {
      const id = String(feature.properties?.id ?? '')
      const isClosed = closedSegmentIds.includes(id)
      const roadClass = String(feature.properties?.class ?? '')
      const color = isClosed ? '#EF4444' : roadClassColors[roadClass] ?? defaultRoadClassColor
      const path = toPath(feature.geometry.coordinates as number[][])

      const polyline = new google.maps.Polyline({
        path,
        map,
        strokeColor: color,
        strokeOpacity: isClosed ? 0 : 0.95,
        strokeWeight: roadClass === 'HAUL_PRIMARY' ? 5 : roadClass === 'HAUL_RAMP' ? 4.5 : 3,
        icons: isClosed ? dashIcons('#EF4444', 4) : undefined,
        clickable: isInteractive,
        zIndex: 2,
      })
      if (isInteractive) {
        polyline.addListener('click', () =>
          onSegmentClickRef.current?.(id, (feature.properties?.name as string) ?? null),
        )
        polyline.addListener('mouseover', () => map.setOptions({ draggableCursor: 'pointer' }))
        polyline.addListener('mouseout', () => map.setOptions({ draggableCursor: '' }))
      }
      overlaysRef.current.push(polyline)
    })

    points(graph).forEach((feature) => {
      const [lng, lat] = feature.geometry.coordinates as number[]
      const isAccess = feature.properties?.kind === 'ACCESS_POINT'
      const marker = new google.maps.Marker({
        position: { lat, lng },
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: isAccess ? 6 : 3,
          fillColor: isAccess ? '#F59E0B' : '#888888',
          fillOpacity: 1,
          strokeColor: '#1a1917',
          strokeWeight: 1.5,
        },
        clickable: false,
        zIndex: 1,
      })
      overlaysRef.current.push(marker)
    })

    lineStrings(draftGraph).forEach((feature) => {
      const draftLine = new google.maps.Polyline({
        path: toPath(feature.geometry.coordinates as number[][]),
        map,
        strokeOpacity: 0,
        icons: dashIcons('#FBBF24', 3),
        clickable: false,
        zIndex: 3,
      })
      overlaysRef.current.push(draftLine)
    })

    if (routeGeometry?.coordinates?.length) {
      const routeLine = new google.maps.Polyline({
        path: toPath(routeGeometry.coordinates),
        map,
        strokeColor: '#F59E0B',
        strokeOpacity: 0.95,
        strokeWeight: 5,
        zIndex: 4,
      })
      overlaysRef.current.push(routeLine)
    }

    if (proposalGeometry?.coordinates) {
      const proposalLine = new google.maps.Polyline({
        path: toPath(proposalGeometry.coordinates as number[][]),
        map,
        strokeColor: '#14B8A6',
        strokeOpacity: 0,
        icons: dashIcons('#14B8A6', 4),
        clickable: false,
        zIndex: 4,
      })
      overlaysRef.current.push(proposalLine)
    }

    if (drawCoordinates.length) {
      if (drawCoordinates.length > 1) {
        const drawLine = new google.maps.Polyline({
          path: toPath(drawCoordinates),
          map,
          strokeColor: '#FBBF24',
          strokeOpacity: 0.9,
          strokeWeight: 3,
          zIndex: 5,
        })
        overlaysRef.current.push(drawLine)
      }
      drawCoordinates.forEach((coord) => {
        const vertex = new google.maps.Marker({
          position: toLatLng(coord),
          map,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 5,
            fillColor: '#FBBF24',
            fillOpacity: 1,
            strokeColor: '#fff',
            strokeWeight: 1,
          },
          clickable: false,
          zIndex: 6,
        })
        overlaysRef.current.push(vertex)
      })
    }

    return clearOverlays
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph, draftGraph, closedKey, routeGeometry, proposalGeometry, drawKey, mode])

  return <MapShell ref={ref} data-testid='sn-network-map' />
}

export const NetworkMap = (props: INetworkMapProps) => (
  <Wrapper
    apiKey={process.env.REACT_APP_GOOGLE_KEY}
    render={(status) =>
      status === Status.SUCCESS ? <NetworkMapInner {...props} /> : <MapLoading aria-hidden />
    }
  />
)
